import axios from '@/utilities/api/axios';
import Cookies from 'js-cookie';
import store from '../store';

export const state = initialState();

export const actions = {
  login: async ({ commit }, payload) => {
    try {
      const res = await axios.post(`/auth/login`, payload);
      //set expiration to an hour from now
      Cookies.set('bearerToken', res.data, { expires: 1 / 3 });
      commit('mutateUserProfileError', '');
      return res;
    } catch (err) {
      // console.error(err);
      commit('mutateUserProfileError', 'There was an issue logging in');
      return err.response.data;
    }
  },

  logout: async ({ commit }) => {
    let res;
    try {
      res = await axios.get(`/auth/logout`);
    } catch (err) {
      console.error(err);
    } finally {
      Cookies.remove('bearerToken');
      Cookies.remove('ksBearerToken');
      commit('mutateUserProfile', { user: {} });
      commit('mutateNeedsPassword', { user: {} });
      store.dispatch('resetProfileProjectFilters');
      localStorage.removeItem('vuex');
      // Signal other tabs to logout
      localStorage.setItem('logout_event', Date.now().toString());
    }
    return res;
  },

  getUserProfile: async ({ commit }) => {
    try {
      const res = await axios.get(`/auth/user`);
      commit('mutateUserProfile', res.data);
      commit('mutateNeedsPassword', res.data);

      return res;
    } catch (err) {
      commit('mutateUserProfile', { user: {} });
      commit('mutateNeedsPassword', { user: {} });
      return;
    }
  },

  updateProfileEnrollment: async ({ commit }, payload) => {
    try {
      const { profileId, projectGroupId, query, templateFormId } = payload;
      const { data } = await axios.post(
        `/profiles/${profileId}/enrollment/${projectGroupId}?step=${query.step}`
      );

      commit('updateEnrollments', {
        ...data.data,
        step: parseInt(data.data.step),
        templateFormId: `#${templateFormId}`
      });
      return data;
    } catch (error) {
      console.error(error);
    }
  },

  deleteMemberSteps: async ({ commit }, payload) => {
    const { profileId, projectGroupId } = payload;
    await axios.delete(
      `/profiles/${profileId}/enrollment/${projectGroupId}`
    );

    commit('deleteEnrollment', { projectGroupId });
  },

  impersonatorLogin: async (_, payload) => {
    try {
      const res = await axios.post(
        `/auth/impersonator-login`,
        payload
      );
      //set expiration to an hour from now
      Cookies.set('ksBearerToken', res.data, { expires: 1 / 3 });

      return res;
    } catch (err) {
      console.error(err);
      return err.response.data;
    }
  },

  updateNeedsPassword: ({ commit }, value) => {
    commit('mutateNeedsPassword', value);
  }
};

export const mutations = {
  mutateUserProfile: (state, payload) => {
    state.userProfile = payload.user;

    store.commit('mutateProfileSubscriptions', payload.user);
  },

  mutateNeedsPassword: (state, payload) => {
    if (typeof payload === 'boolean') {
      state.needNewPassword = payload;
    } else if (payload?.user?.account) {
      state.needNewPassword = payload.user.account.changePasswordAtNextLogon;
    } else {
      state.needNewPassword = true;
    }
  },

  mutateProfileSubscriptions: async (state, payload) => {
    if (!payload?.subscribedTo || !payload?.subscription) {
      state.subscriptions = {};
    } else if (payload.subscription.name === 'UNLIMITED') {
      const subscriptions = {
        canCreateProject: true,
        canCreateItinerary: true,
        hasReachedCapacityLimit: false,
        canCreateTemplates: true
      };

      state.subscriptions = subscriptions;
    } else if (payload.subscription.name === 'PRO') {
      const subscriptions = {
        canCreateProject: true,
        canCreateItinerary: true,
        hasReachedCapacityLimit: false,
        canCreateTemplates: true
      };

      state.subscriptions = subscriptions;
    } else if (payload.subscription.name === 'STANDARD') {
      const subscriptions = {
        canCreateProject: false,
        canCreateItinerary: false,
        hasReachedCapacityLimit: false,
        canCreateTemplates: false
      };

      state.subscriptions = subscriptions;
    } else {
      const subscriptions = {};
      const {
        cloudStorageUsed,
        itineraryCount,
        projectCount
      } = payload.subscribedTo;

      const {
        cloudStorage,
        maxItineraries,
        maxProjects,
        name
      } = payload.subscription;

      const hasReachedCapacityLimit =
        Math.round((cloudStorageUsed + Number.EPSILON) * 100) / 100 >=
        cloudStorage;

      //check cloud storage
      subscriptions['hasReachedCapacityLimit'] = hasReachedCapacityLimit;

      //check projects
      subscriptions['canCreateProject'] = hasReachedCapacityLimit
        ? false
        : Number(maxProjects) > projectCount;

      //check itineraries
      if (Number(maxItineraries) > itineraryCount && !hasReachedCapacityLimit) {
        subscriptions['canCreateItinerary'] = true;
      }

      if (Number(maxItineraries) <= itineraryCount || hasReachedCapacityLimit) {
        const createItineraryPermissions = [
          'PROJECT_EDIT',
          'PROJECT_GROUP_EDIT'
        ];

        const res = await store.dispatch(
          'getProjectsUserCanSubmitItineraries',
          {
            profileId: payload?.profile?.profileId.replace('#', ''),
            language: payload?.profile?.defaultLanguage
          }
        );

        //check itinerary projects
        const itineraryAvailableProjects = res.data.list.filter(
          profileProject => {
            const { project, memberOfs, projectFounder } = profileProject;

            const permissions = memberOfs
              .map(memberOf => memberOf.memberOf.permissions)
              .flat();

            return (
              createItineraryPermissions.some(perm =>
                permissions.includes(perm)
              ) &&
              !project?.archived &&
              !projectFounder?.isCloudStorageFull &&
              !project?.mainProject &&
              !projectFounder?.reachedMaxItineraries
            );
          }
        );

        subscriptions['canCreateItinerariesOtherProjects'] =
          itineraryAvailableProjects.length > 0;

        const documentAvailableProjects = res.data.list.filter(
          profileProject => {
            const { project, memberOfs, projectFounder } = profileProject;

            const permissions = memberOfs
              .map(memberOf => memberOf.memberOf.permissions)
              .flat();

            return (
              permissions.includes('DOCUMENT_SUBMIT') &&
              !project?.archived &&
              !projectFounder?.isCloudStorageFull &&
              !project?.mainProject
            );
          }
        );

        subscriptions['canCreateDocumentsOtherProjects'] =
          documentAvailableProjects.length > 0;

        subscriptions[
          'projectsCanSubmitDocuments'
        ] = documentAvailableProjects.map(project => project.project.projectId);
      }

      //check templates
      const canCreateTemplatesSubscriptions = [
        'PRO',
        'FREE',
        'PRIME',
        'SILVER',
        'GOLD'
      ];

      subscriptions[
        'canCreateTemplates'
      ] = canCreateTemplatesSubscriptions.includes(name);

      state.subscriptions = subscriptions;
    }
  },

  mutateUserProfileError: (state, payload) => {
    state.authError = payload;
  },

  updateEnrollments: (state, payload) => {
    const isEnrollment = state?.userProfile?.profile?.enrollment;
    if (isEnrollment) {
      const filteredEnrollment = state.userProfile.profile.enrollment.filter(
        enroll => enroll.templateFormId !== payload.templateFormId
      );
      state.userProfile.profile.enrollment = [...filteredEnrollment, payload];
    }
  },

  deleteEnrollment: (state, payload) => {
    const isEnrollment = state?.userProfile?.profile?.enrollment;
    if (isEnrollment) {
      const filteredEnrollment = state.userProfile.profile.enrollment.filter(
        enroll =>
          enroll.projectGroupId.replace('#', '') !== payload.projectGroupId
      );
      if (filteredEnrollment.length === 0) {
        state.userProfile.profile.enrollment = null;
      } else {
        state.userProfile.profile.enrollment = filteredEnrollment;
      }
    }
  }
};

export const getters = {};

function initialState() {
  return {
    needNewPassword: false,
    userProfile: {},
    authError: '',
    subscriptions: {
      canCreateProject: false,
      canCreateTemplates: false,
      canCreateItinerary: false,
      canCreateItinerariesOtherProjects: false,
      canCreateDocumentsOtherProjects: false,
      hasReachedCapacityLimit: false,
      projectsCanSubmitDocuments: []
    }
  };
}
